import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { AdminShell } from '../../components/AdminShell';
import { loadAllShops, onboardingStage, colourForShop } from '../../lib/adminQueries';

const initials = (name) => (name || '').split(/\s+/).filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';

const STAGES = [
  { key: 'account_created', label: 'Account created', dot: '#9499a6', tag: { cls: 'gray',  text: 'Step 1/4' } },
  { key: 'profile_set_up',  label: 'Profile set up',  dot: '#f59e0b', tag: { cls: 'amber', text: 'Step 2/4' } },
  { key: 'stripe_kyc',      label: 'Stripe KYC',      dot: '#4f46ff', tag: { cls: 'blue',  text: 'Step 3/4' } },
  { key: 'live',            label: 'Live',            dot: '#10b981', tag: { cls: 'green', text: 'Live' } },
];

const STAGE_PROGRESS = { account_created: 25, profile_set_up: 50, stripe_kyc: 75, live: 100 };

const OnboardingInner = () => {
  const [shops, setShops] = useState([]);
  const [stageMap, setStageMap] = useState({});  // shop_id → stage key
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const all = await loadAllShops();
      // Need to know which shops have services (for profile_set_up signal)
      const ids = all.map(x => x.id);
      const hasServices = new Set();
      if (ids.length) {
        const { data: svc } = await supabase.from('services').select('shop_id').in('shop_id', ids).eq('active', true);
        (svc || []).forEach(s => hasServices.add(s.shop_id));
      }
      const map = {};
      all.forEach(sh => { map[sh.id] = onboardingStage(sh, hasServices.has(sh.id)); });
      setShops(all);
      setStageMap(map);
      setLoading(false);
    })();
  }, []);

  const byStage = useMemo(() => {
    const groups = { account_created: [], profile_set_up: [], stripe_kyc: [], live: [] };
    shops.forEach(sh => { (groups[stageMap[sh.id]] || groups.account_created).push(sh); });
    return groups;
  }, [shops, stageMap]);

  return (
    <main className="main admin-page" data-testid="admin-onboarding-page">
      <div className="topbar">
        <div>
          <h2>Onboarding</h2>
          <div className="sub">Where each new shop is in the pipeline</div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <Link to="/admin/onboard" className="btn p" data-testid="onboarding-new-shop">
            + Onboard new shop
          </Link>
        </div>
      </div>

      <div className="body">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--mute)' }}>Loading…</div>
        ) : (
          <div className="pipe" data-testid="onboarding-kanban">
            {STAGES.map(stage => {
              const items = byStage[stage.key] || [];
              return (
                <div key={stage.key} className="stage" data-testid={`stage-${stage.key}`}>
                  <div className="stage-h">
                    <span className="nm">
                      <span className="dot" style={{ background: stage.dot }} />
                      {stage.label}
                    </span>
                    <span className="ct">{items.length}</span>
                  </div>
                  <div className="stage-b">
                    {items.length === 0 && (
                      <div style={{ color: 'var(--text-mute-2)', fontSize: 'var(--fs-sm)', padding: '8px 4px', textAlign: 'center' }}>—</div>
                    )}
                    {items.map(sh => (
                      <Link to={`/admin/shops/${sh.id}`} key={sh.id} className="ocard" data-testid={`ocard-${sh.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                        <div className="nm">
                          <span className="sq" style={{ background: colourForShop(sh) }}>{initials(sh.name).charAt(0)}</span>
                          <div>
                            <b>{sh.name}</b>
                            <small>{sh.subdomain}.instacut.co.uk</small>
                          </div>
                        </div>
                        <div className="pr"><i style={{ width: `${STAGE_PROGRESS[stage.key]}%` }} /></div>
                        <div className="ft">
                          <span>{blockingFor(sh, stage.key)}</span>
                          <span className={`tag ${stage.tag.cls}`}>{stage.tag.text}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
};

function blockingFor(shop, stage) {
  switch (stage) {
    case 'account_created': return 'Needs profile setup';
    case 'profile_set_up':  return 'Needs Stripe KYC';
    case 'stripe_kyc':      return 'Awaiting first booking';
    case 'live':            return 'On Instacut';
    default: return '';
  }
}

const Onboarding = () => <AdminShell><OnboardingInner /></AdminShell>;
export default Onboarding;
